/**
 * ForecastView — /#/forecast
 *
 * BUILD-13 Item 3 — Option B (Computed on-demand)
 * Decision locked: Bryan, Thu May 14 6:23 PM EDT
 * Ships: Sunday May 17 (after migration applied)
 *
 * Changes from previous version:
 *   - Removed ALL hardcoded numbers and fabricated deal names
 *   - Fetches /api/forecast on every mount (recomputed server-side)
 *   - Period selector: this quarter / next quarter / this year
 *   - Group-by toggle: by owner / by stage
 *   - Loading skeleton while computing
 *   - Empty state when no data or no quota
 *   - By-owner table with weighted, gross, quota, attainment %
 *   - By-stage bar chart (CSS-only, no chart library)
 *   - Click on any owner row → /pipeline?owner=<userId>
 *   - Click on any stage row → /pipeline?stage=<stageName>
 *   - Stale-while-revalidate: shows cached value instantly, refreshes in bg
 */

import { useCallback, useEffect, useRef, useState, Component } from "react";
import type { ReactNode, ErrorInfo } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { getSession } from "@/lib/session";
import { friendlyError } from "@/lib/friendly-error";
import { buildDrillUrl } from "@/lib/drillUrl";
import { getHealthColor, HEALTH_PALETTE } from "@shared/schema";

// ── Constants ──────────────────────────────────────────────────────────────
const BLACK = "#0B0B0F";
const RED = "#DC2626"; // Brand red — not score-driven.
const BG = "#FAFAF7";
// GREEN/AMBER legacy locals replaced with canon palette via @shared/schema.
const GREEN = HEALTH_PALETTE.darkGreen;
const AMBER = HEALTH_PALETTE.amber;
const BORDER = "#E5E5E7";
const MUTED = "#8E8E94";
const SUBTLE = "#6B6B70";

// ── Types ──────────────────────────────────────────────────────────────────
type ForecastPeriod = "current_quarter" | "next_quarter" | "current_year";
type ForecastGroupBy = "owner" | "stage";

interface ForecastOwnerRow {
  user_id: string;
  name: string;
  email: string;
  weighted: number;
  gross: number;
  quota: number | null;
  attainment_pct: number | null;
  deal_count: number;
}

interface ForecastStageRow {
  stage_name: string;
  probability: number;
  weighted: number;
  gross: number;
  deal_count: number;
}

interface ForecastResult {
  period: ForecastPeriod;
  period_label: string;
  period_start: string;
  period_end: string;
  totals: {
    weighted: number;
    gross: number;
    quota: number | null;
    attainment_pct: number | null;
  };
  by_owner: ForecastOwnerRow[];
  by_stage: ForecastStageRow[];
  cached_at: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────
function fmtUSD(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${Math.round(n / 1_000)}K`;
  return `$${n}`;
}

// Attainment % is score-driven — canon palette per CANON.md §5.
function attainmentColor(pct: number | null): string {
  if (pct == null) return MUTED;
  return getHealthColor(pct);
}

// ── Skeleton ───────────────────────────────────────────────────────────────
function SkeletonTile() {
  return (
    <div style={{ background: "#FFFFFF", border: `1px solid ${BORDER}`, borderRadius: 12, padding: 20 }}>
      <div style={{ height: 10, width: "60%", background: "#F0F0F0", borderRadius: 4, marginBottom: 12 }} />
      <div style={{ height: 28, width: "40%", background: "#F0F0F0", borderRadius: 4 }} />
    </div>
  );
}

function SkeletonRow() {
  return (
    <tr>
      {[1,2,3,4,5].map(i => (
        <td key={i} style={{ padding: "12px 14px" }}>
          <div style={{ height: 12, background: "#F0F0F0", borderRadius: 3 }} />
        </td>
      ))}
    </tr>
  );
}

// ── Attainment bar ─────────────────────────────────────────────────────────
function AttainmentBar({ pct }: { pct: number | null }) {
  if (pct == null) {
    return <span style={{ color: MUTED, fontSize: 12 }}>—</span>;
  }
  const capped = Math.min(pct, 100);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: "#F0F0F0", borderRadius: 3, minWidth: 60 }}>
        <div style={{
          width: `${capped}%`,
          height: "100%",
          background: attainmentColor(pct),
          borderRadius: 3,
          transition: "width 0.3s ease",
        }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color: attainmentColor(pct), minWidth: 36, textAlign: "right" }}>
        {pct}%
      </span>
    </div>
  );
}

// ── Stage probability bar ──────────────────────────────────────────────────
function ProbBadge({ prob }: { prob: number }) {
  const pct = Math.round(prob * 100);
  const color = pct >= 75 ? GREEN : pct >= 40 ? AMBER : pct === 0 ? MUTED : "#6B6B70";
  return (
    <span style={{
      display: "inline-block",
      padding: "2px 7px",
      borderRadius: 99,
      background: `${color}18`,
      color,
      fontSize: 11,
      fontWeight: 700,
    }}>
      {pct}%
    </span>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
// W33 Fix 3: per-route error boundary so a render crash on /forecast
// shows a meaningful recovery message instead of the generic Sentry fallback.
class ForecastErrorBoundary extends Component<
  { children: ReactNode },
  { caught: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { caught: false };
  }
  override componentDidCatch(_err: Error, _info: ErrorInfo) {
    this.setState({ caught: true });
  }
  override render() {
    if (this.state.caught) {
      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "Inter, system-ui, sans-serif",
            background: "#FAFAF7",
            color: "#0B0B0F",
            gap: 16,
            padding: 24,
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 20, fontWeight: 700 }}>Forecast is being repaired.</div>
          <div style={{ fontSize: 14, color: "#6B6B70", maxWidth: 420 }}>
            The forecast view encountered an error and could not load. The team has been
            notified and a fix is in progress.
          </div>
          <a
            href="/#/"
            style={{
              marginTop: 8,
              padding: "10px 24px",
              borderRadius: 8,
              background: "#0B0B0F",
              color: "#fff",
              textDecoration: "none",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Return to Dashboard
          </a>
          <p style={{ fontSize: 11, color: "#8E8E94", marginTop: 8 }}>
            Attilius Support · Mi6 Group
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

function ForecastViewInner() {
  const [period, setPeriod] = useState<ForecastPeriod>("current_quarter");
  const [groupBy, setGroupBy] = useState<ForecastGroupBy>("owner");
  const [data, setData] = useState<ForecastResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const staleRef = useRef<ForecastResult | null>(null);

  const fetchForecast = useCallback(
    async (p: ForecastPeriod, g: ForecastGroupBy, silent = false) => {
      if (!silent) setLoading(true);
      setError(null);
      try {
        const session = getSession();
        const token = session?.token ?? "";
        const url = `/api/forecast?period=${p}&group_by=${g}`;
        const resp = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!resp.ok) {
          // 404 is the expected empty-state path while the forecast handler
          // is deferred (see server/routes.ts forecast note). Treat it as
          // a quiet empty state rather than a noisy console error.
          if (resp.status === 404) {
            staleRef.current = null;
            setData(null);
            setError(null);
            return;
          }
          const body = await resp.json().catch(() => ({}));
          throw new Error(body.error ?? `HTTP ${resp.status}`);
        }
        const result: ForecastResult = await resp.json();
        staleRef.current = result;
        setData(result);
      } catch (err: any) {
        // Silent in console — banner handles user-facing messaging.
        setError(friendlyError(err));
        // keep stale data visible
        if (staleRef.current && silent) setData(staleRef.current);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    []
  );

  // Load on mount and whenever period/group_by changes
  useEffect(() => {
    // Show stale while revalidating
    if (staleRef.current) {
      setData(staleRef.current);
      setLoading(false);
      fetchForecast(period, groupBy, true); // silent background refresh
    } else {
      fetchForecast(period, groupBy, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, groupBy]);

  // BUILD-14: drill-downs now include period + from/fromLabel for breadcrumb
  const handleOwnerClick = (userId: string) => {
    window.location.hash = buildDrillUrl('/pipeline', {
      owner: userId,
      period,
      from: '/forecast',
      fromLabel: 'Forecast',
    });
  };

  const handleStageClick = (stageName: string) => {
    window.location.hash = buildDrillUrl('/pipeline', {
      stage: stageName,
      period,
      from: '/forecast',
      fromLabel: 'Forecast',
    });
  };

  const noData =
    !loading &&
    !error &&
    data &&
    data.totals.weighted === 0 &&
    data.by_owner.length === 0;

  const noQuota = !loading && data && data.totals.quota == null;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <OperatorShell breadcrumbs={[{ label: "Forecast" }]}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>

        {/* ── Header + controls ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 28, flexWrap: "wrap", justifyContent: "space-between" }}>
          <h1 style={{ fontSize: 26, fontWeight: 900, color: BLACK, margin: 0, letterSpacing: "-0.01em" }}>
            Forecast
            {data?.period_label && (
              <span style={{ fontWeight: 400, color: SUBTLE, marginLeft: 12, fontSize: 20 }}>
                — {data.period_label}
              </span>
            )}
          </h1>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {/* Period selector */}
            {(["current_quarter", "next_quarter", "current_year"] as ForecastPeriod[]).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                style={{
                  padding: "7px 14px",
                  borderRadius: 8,
                  border: `1.5px solid ${period === p ? BLACK : BORDER}`,
                  background: period === p ? BLACK : "#FFFFFF",
                  color: period === p ? "#FFFFFF" : BLACK,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  letterSpacing: "0.01em",
                  transition: "all 0.15s",
                }}
              >
                {p === "current_quarter" ? "This Quarter"
                  : p === "next_quarter" ? "Next Quarter"
                  : "This Year"}
              </button>
            ))}

            {/* Group-by toggle */}
            <div style={{ display: "flex", border: `1.5px solid ${BORDER}`, borderRadius: 8, overflow: "hidden", marginLeft: 8, flexWrap: "wrap" }}>
              {(["owner", "stage"] as ForecastGroupBy[]).map((g) => (
                <button
                  key={g}
                  onClick={() => setGroupBy(g)}
                  style={{
                    padding: "7px 14px",
                    background: groupBy === g ? BLACK : "#FFFFFF",
                    color: groupBy === g ? "#FFFFFF" : BLACK,
                    border: "none",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  By {g === "owner" ? "Owner" : "Stage"}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Error banner ── */}
        {error && (
          <div style={{ background: "rgba(220,38,38,0.10)", border: `1px solid ${RED}`, borderRadius: 10, padding: "12px 16px", marginBottom: 24, fontSize: 13, color: "#0B0B0F" }}>
            Forecast could not load: {error.replace(/\.$/, "")}. Showing last known values.
          </div>
        )}

        {/* ── Top-line KPI tiles ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 16, marginBottom: 32 }}>
          {loading ? (
            <>
              <SkeletonTile />
              <SkeletonTile />
              <SkeletonTile />
              <SkeletonTile />
            </>
          ) : (
            <>
              {/* Weighted Pipeline */}
              <div style={{ background: "#FFFFFF", border: `1px solid ${BORDER}`, borderRadius: 12, padding: 20, borderTop: `3px solid ${AMBER}` }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED, marginBottom: 8 }}>
                  Weighted Pipeline
                </div>
                {data && data.totals.weighted > 0 ? (
                  <>
                    <div style={{ fontSize: 28, fontWeight: 900, color: BLACK, lineHeight: 1 }}>{fmtUSD(data.totals.weighted)}</div>
                    <div style={{ fontSize: 12, color: SUBTLE, marginTop: 4 }}>probability-weighted</div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 20, fontWeight: 700, color: "#C9C9CC", lineHeight: 1 }}>$0</div>
                    <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>No open deals in this period</div>
                  </>
                )}
              </div>

              {/* Gross Pipeline */}
              <div style={{ background: "#FFFFFF", border: `1px solid ${BORDER}`, borderRadius: 12, padding: 20, borderTop: `3px solid #6B6B70` }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED, marginBottom: 8 }}>
                  Gross Pipeline
                </div>
                {data && data.totals.gross > 0 ? (
                  <>
                    <div style={{ fontSize: 28, fontWeight: 900, color: BLACK, lineHeight: 1 }}>{fmtUSD(data.totals.gross)}</div>
                    <div style={{ fontSize: 12, color: SUBTLE, marginTop: 4 }}>unweighted deal value</div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 20, fontWeight: 700, color: "#C9C9CC", lineHeight: 1 }}>$0</div>
                    <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>No deals with close dates in period</div>
                  </>
                )}
              </div>

              {/* Quota — W24: removed off-canon #6B21A8 purple (CANON_VISUALS_LOCKED §4) */}
              <div style={{ background: "#FFFFFF", border: `1px solid ${BORDER}`, borderRadius: 12, padding: 20 }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED, marginBottom: 8 }}>
                  Team Quota
                </div>
                {data?.totals.quota != null ? (
                  <>
                    <div style={{ fontSize: 28, fontWeight: 900, color: BLACK, lineHeight: 1 }}>{fmtUSD(data.totals.quota)}</div>
                    <div style={{ fontSize: 12, color: SUBTLE, marginTop: 4 }}>combined team quota</div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 20, fontWeight: 700, color: "#C9C9CC", lineHeight: 1 }}>—</div>
                    <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>Set quota_annual on users to activate</div>
                  </>
                )}
              </div>

              {/* Attainment */}
              <div style={{ background: "#FFFFFF", border: `1px solid ${BORDER}`, borderRadius: 12, padding: 20, borderTop: `3px solid ${attainmentColor(data?.totals.attainment_pct ?? null)}` }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED, marginBottom: 8 }}>
                  Attainment
                </div>
                {data?.totals.attainment_pct != null ? (
                  <>
                    <div style={{ fontSize: 28, fontWeight: 900, color: attainmentColor(data.totals.attainment_pct), lineHeight: 1 }}>
                      {data.totals.attainment_pct}%
                    </div>
                    <div style={{ fontSize: 12, color: SUBTLE, marginTop: 4 }}>weighted ÷ quota</div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 20, fontWeight: 700, color: "#C9C9CC", lineHeight: 1 }}>—</div>
                    <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>Quota not configured</div>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* ── No-quota notice ── */}
        {noQuota && !noData && (
          <div style={{ background: "rgba(234,88,12,0.10)", border: `1px solid #EA580C`, borderRadius: 10, padding: "12px 16px", marginBottom: 24, fontSize: 13, color: "#0B0B0F" }}>
            Quota not configured. Set <code style={{ fontSize: 12 }}>quota_annual</code> on user records to enable attainment tracking.
          </div>
        )}

        {/* ── No-data empty state ── */}
        {noData && (
          <div style={{ background: "#FFFFFF", border: `1px solid ${BORDER}`, borderRadius: 12, padding: "48px 24px", textAlign: "center", marginBottom: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: SUBTLE, marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.18em" }}>Empty</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: BLACK, marginBottom: 8 }}>No open deals in this period</div>
            <div style={{ fontSize: 13, color: SUBTLE, maxWidth: 420, margin: "0 auto" }}>
              Add opportunities with close dates in the selected period and set their stage to populate the forecast.
            </div>
          </div>
        )}

        {/* ── By-owner table ── */}
        {groupBy === "owner" && !noData && (
          <div style={{ background: "#FFFFFF", border: `1px solid ${BORDER}`, borderRadius: 12, overflow: "hidden", marginBottom: 24 }}>
            <div style={{ padding: "14px 20px", borderBottom: `1px solid ${BORDER}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: BLACK }}>By Owner</div>
              <div style={{ fontSize: 11, color: SUBTLE }}>Click a row to drill into that rep's pipeline</div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#FAFAF7" }}>
                    {["Rep", "Deals", "Gross Pipeline", "Weighted", "Quota", "Attainment"].map((h) => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: MUTED, borderBottom: `1px solid ${BORDER}`, whiteSpace: "nowrap" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <>
                      <SkeletonRow />
                      <SkeletonRow />
                      <SkeletonRow />
                    </>
                  ) : (
                    data?.by_owner.map((row) => (
                      <tr
                        key={row.user_id}
                        onClick={() => handleOwnerClick(row.user_id)}
                        style={{ borderBottom: `1px solid #F5F5F5`, cursor: "pointer", transition: "background 0.1s" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#F8F8F8")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "")}
                      >
                        <td style={{ padding: "12px 14px", fontWeight: 600, color: BLACK }}>
                          {row.name}
                          <div style={{ fontSize: 11, color: MUTED, fontWeight: 400 }}>{row.email}</div>
                        </td>
                        <td style={{ padding: "12px 14px", color: SUBTLE, textAlign: "center" }}>{row.deal_count}</td>
                        <td style={{ padding: "12px 14px", color: BLACK, fontWeight: 600 }}>{fmtUSD(row.gross)}</td>
                        <td style={{ padding: "12px 14px", color: BLACK, fontWeight: 700 }}>{fmtUSD(row.weighted)}</td>
                        <td style={{ padding: "12px 14px", color: SUBTLE }}>
                          {row.quota != null ? fmtUSD(row.quota) : <span style={{ color: MUTED }}>—</span>}
                        </td>
                        <td style={{ padding: "12px 14px", minWidth: 120 }}>
                          <AttainmentBar pct={row.attainment_pct} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── By-stage view ── */}
        {groupBy === "stage" && !noData && (
          <div style={{ background: "#FFFFFF", border: `1px solid ${BORDER}`, borderRadius: 12, overflow: "hidden", marginBottom: 24 }}>
            <div style={{ padding: "14px 20px", borderBottom: `1px solid ${BORDER}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: BLACK }}>By Stage</div>
              <div style={{ fontSize: 11, color: SUBTLE }}>Click a row to drill into that stage's deals</div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#FAFAF7" }}>
                    {["Stage", "Probability", "Deals", "Gross Pipeline", "Weighted Value"].map((h) => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: MUTED, borderBottom: `1px solid ${BORDER}`, whiteSpace: "nowrap" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <>
                      <SkeletonRow />
                      <SkeletonRow />
                      <SkeletonRow />
                    </>
                  ) : (
                    data?.by_stage.map((row) => {
                      return (
                        <tr
                          key={row.stage_name}
                          onClick={() => handleStageClick(row.stage_name)}
                          style={{ borderBottom: `1px solid #F5F5F5`, cursor: "pointer", transition: "background 0.1s" }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#F8F8F8")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "")}
                        >
                          <td style={{ padding: "12px 14px", fontWeight: 600, color: BLACK }}>{row.stage_name}</td>
                          <td style={{ padding: "12px 14px" }}><ProbBadge prob={row.probability} /></td>
                          <td style={{ padding: "12px 14px", color: SUBTLE, textAlign: "center" }}>{row.deal_count}</td>
                          <td style={{ padding: "12px 14px", color: SUBTLE }}>{fmtUSD(row.gross)}</td>
                          {/* W24: replaced amber bar chart with text-only per CANON_VISUALS_LOCKED §2 (forecast surfaces text-only) */}
                          <td style={{ padding: "12px 14px", textAlign: "right" }}>
                            <span style={{ fontWeight: 700, color: BLACK }}>{fmtUSD(row.weighted)}</span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Cached-at footer ── */}
        {data && !loading && (
          <div style={{ textAlign: "right", fontSize: 11, color: MUTED, marginTop: 8 }}>
            Computed {new Date(data.cached_at ?? Date.now()).toLocaleTimeString("en-US", { timeZone: "America/Detroit", hour: "2-digit", minute: "2-digit" })} — refreshes every 60 s
          </div>
        )}

      </div>
    </OperatorShell>
  );
}

// W33 Fix 3: export default wraps inner component with per-route error boundary.
export default function ForecastView() {
  return (
    <ForecastErrorBoundary>
      <ForecastViewInner />
    </ForecastErrorBoundary>
  );
}
